<template lang="pug">

  .FilterSidebar(@click="onClose")
    transition(name="slide")
      .filter-sidebar-content(
        v-if="PAGE_SHOW"
        @click.stop)
        .header
          .title {{$t('generic.field.filter')}}
        .filter-list
          .filter-item(
            v-for="(filter, index) in filters"
            :key="index"
            :class="{'open': filter.isOpen}"
            @click="onFilterItemClick(filter, index)")
            template(v-if="filter.isVeryCustom")
              slot(:name="filter.veryCustomSlotName" :filter="filter")
            template(v-if="isToggleBoxFilter(filter)")
              .filter
                ToggleBox(
                  v-model="filter.value"
                  :label="filter.customLabel")
            template(v-else)
              .form-title
                h5 {{ filter.label }}
              transition(name="upwards-fade-in")
                .selection-count(v-if="selectionCountVisible(filter)")
                  | {{ $t('generic.msg.selected_count', [filter.selectedItemCount]) }}
              .title-icon
                IconDownLine
              transition(name="fade-scale")
                CategoryTree.category-tree(
                  v-if="isCategoryFilter(filter) && filter.isOpen"
                  :categories="filter.options"
                  :itemMode="categoryTreeItemMode"
                  :selectionPathDirection="categoryTreePathDirection"
                  @input="onSelectedCategoryChange(filter, ...arguments)"
                  :value="filter.value")
                SelectionBox.selection(
                  v-if="filter.isOpen"
                  :options="filter.options"
                  :searchKeyword="filter.searchKeyword"
                  :labelKey="filter.labelKey"
                  :selectKey="filter.selectKey"
                  :disabledKey="filter.disabledKey"
                  @search="onSearchKeywordChange(filter, ...arguments)"
                  @selectionChange="onSelectionChange(filter, ...arguments)")
        .footer
          .clear(@click="onClear")
            IconClear
          button.btn.btn-primary.apply(
            type="button"
            @click="onClose")
            span {{$t('generic.action.apply')}}

</template>

<script>

  import { mapState } from 'vuex'

  import SelectionBox from '@/component/selection-box'
  import CategoryTree from '@/component/category-tree'
  import { CategoryTreeItemMode, CategoryTreeDirection } from '@/component/category-tree/model'
  import { FilterTypes } from './model'
  import hasha from 'hasha'
  import { turkishSort } from '@/utils/helper'

  export default {
    name: "FilterSidebar",

    components: {
      SelectionBox,
      CategoryTree
    },

    props: {
      filters: {
        type: Array,
        required: true
      }
    },

    data () {
      return {
        PAGE_SHOW: false,
        initialFiltersHash: null
      }
    },

    computed: {
      ...mapState('Branch', [
        'branch_list'
      ]),

      categoryTreeItemMode () {
        return CategoryTreeItemMode.CHECKBOX
      },

      categoryTreePathDirection () {
        return CategoryTreeDirection.LOWER
      },

      terminalList () {
        let stateBranchList = this.branch_list.list
        let branch_list = []
        let new_list = []
        stateBranchList.map((sb, indexOne) => {
          if (sb.terminals.length) {
            branch_list[indexOne] = {
              level: 0,
              data: {
                id: sb.id,
                name: sb.name,
                level: 0,
                terminal: []
              }
            }
            let terminal = []
            sb.terminals.map(sbt => {
              if (sbt.type === this.terminalTypes.NORMAL) {
                terminal.push({
                  level: 0,
                  id: sbt.id,
                  name: sbt.name
                })
              }
            })
            branch_list[indexOne].data.terminal = terminal
          }
        })
        let parentChildRelationIndex = 0
        branch_list.map((bl, index) => {
          new_list.push({
            parent: true,
            selected: false,
            parentChildRelationIndex: parentChildRelationIndex,
            level: 0,
            data: {
              id: bl.data.id,
              name: bl.data.name,
              level: 0,
              terminal: bl.data.terminal
            }
          })
          if (bl.data.terminal) {
            bl.data.terminal.sort((b1 , b2) => turkishSort(b1.name , b2.name)).map(bt => {
              new_list.push({
                parent: false,
                selected: false,
                parentChildRelationIndex: parentChildRelationIndex,
                level: 1,
                data: {
                  id: bt.id,
                  name: bt.name,
                  level: 0,
                  terminal: []
                }
              })
            })
          }
          parentChildRelationIndex = parentChildRelationIndex + 1
        })
        return new_list
      }
    },

    methods: {
      onSearchKeywordChange (filter, value) {
        filter.searchKeyword = value
      },

      onFilterItemClick (filter, index) {
        filter.isOpen = !filter.isOpen
        if (filter.isOpen)
          this.filters.forEach((filter, filterIndex) => {
            filter.isOpen = filterIndex === index
          })
      },

      onSelectionChange (filter, option, value) {
        if (value)
          filter.value.push(option[filter.idKey])
        else
          filter.value.splice(
            filter.value.findIndex(id => id === option[filter.idKey]),
            1
          )
        this.animateSelectionCount(filter)
      },

      onSelectedCategoryChange (filter, value) {
        filter.value = value
        this.animateSelectionCount(filter)
      },

      onSelectedBranchTreeChange (filter, value) {
        filter.value = value
        this.animateSelectionCount(filter)
      },

      onClose () {
        const filtersHash = hasha(JSON.stringify(this.filters))
        const hasChange = this.initialFiltersHash !== filtersHash
        this.$emit('close', hasChange)
      },

      onClear () {
        this.filters.forEach(filter => filter.clear())
        this.terminalList.forEach(terminal => terminal.selected = false)
      },

      selectionCountVisible (filter) {
        return filter.selectedItemCount && !filter.animatingSelectionCount
      },

      animateSelectionCount (filter) {
        filter.animatingSelectionCount = true
        setTimeout(() => {
          filter.animatingSelectionCount = false
        }, 350)
      },

      isCategoryFilter (filter) {
        return filter.type === FilterTypes.CATEGORY
      },

      isBranchTreeFilter (filter) {
        return filter.type === FilterTypes.BRANCH_TREE
      },

      isToggleBoxFilter(filter) {
        return filter.type === FilterTypes.TOGGLE_BOX
      }
    },

    mounted () {
      this.initialFiltersHash = hasha(JSON.stringify(this.filters))
      setTimeout(() => {
        this.PAGE_SHOW = true
      }, 150)
    }
  }
</script>

<style lang="scss" scoped>

  .FilterSidebar {
    position: fixed;
    top: 0;
    left: 0;
    width: 100vw;
    height: 100vh;
    background-color: rgba(black, 0.5);
    z-index: 1000;
    display: flex;
    justify-content: flex-end;

    .filter-sidebar-content {
      width: 450px;
      height: 100%;
      background-color: white;
      display: flex;
      flex-direction: column;

      .header {
        flex: 0 0 auto;
        height: 70px;
        border-bottom: 1px solid $color-hide;
        display: flex;
        justify-content: center;
        align-items: center;

        .title {
          color: $color-navy;
          font-weight: 600;
          font-size: 17px;
        }
      }

      .filter-list {
        flex: 1 1 auto;
        height: calc(100% - 140px);
        overflow-y: auto;

        .filter-item {
          border-bottom: 1px solid $color-hide;
          display: flex;
          align-items: center;
          padding: 24px 32px;
          cursor: pointer;
          flex-wrap: wrap;
          user-select: none;

          .form-title {
            flex: 3 1 auto;
            display: flex;
            align-items: center;
            cursor: pointer;

            h5 {
              font-weight: bold;
            }
          }

          .selection-count {
            flex: 1 1 auto;
            color: $color-blue;
            text-align: right;
            padding-right: 10px;
          }

          .title-icon {
            flex: 0 0 auto;
            color: $color-light;
            transform: rotate(-90deg);
            transition: $transition-fast;
          }

          &.open {
            .title-icon {
              transform: rotate(0deg);
            }
          }

          .selection {
            width: 100%;
            margin-top: 25px;
          }

          .category-tree {
            width: 100%;
            margin-top: 25px;
            border: 1px solid $color-hide;
            border-radius: $border-radius;
            max-height: 300px;
            overflow-y: scroll;

            &::-webkit-scrollbar {
              width: 5px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              border-radius: $border-radius;
              background: #e4e4e4;
            }
          }

          .branch-tree {
            width: 100%;
            margin-top: 25px;
            border: 1px solid $color-hide;
            border-radius: $border-radius;
            max-height: 300px;
            overflow-y: scroll;

            &::-webkit-scrollbar {
              width: 5px;
            }

            &::-webkit-scrollbar-track {
              background: transparent;
            }

            &::-webkit-scrollbar-thumb {
              border-radius: $border-radius;
              background: #e4e4e4;
            }
          }
        }
      }

      .footer {
        flex: 0 0 auto;
        height: 70px;
        background-color: #f0f2f4;
        display: flex;
        align-items: center;
        padding: 0 32px;

        .clear {
          flex: 0 0 auto;
          width: 42px;
          height: 42px;
          margin-right: 16px;
          border: 1px solid $color-blue;
          border-radius: $border-radius;
          color: $color-blue;
          display: flex;
          justify-content: center;
          align-items: center;
          cursor: pointer;

          &:hover {
            background-color: rgba($color-blue, 0.1);
          }
        }

        .apply {
          flex: 1 1 auto;
        }
      }

    }

    .upwards-fade-in-enter-active, .upwards-fade-in-leave-active {
      transition: all .3s;
    }

    .upwards-fade-in-enter {
      transform: translateY(50%);
      opacity: 1;
    }

    .upwards-fade-in-enter-to {
      transform: translateY(0);
      opacity: 1;
    }

    .upwards-fade-in-leave-to {
      transform: translateY(-25%);
      opacity: 0;
    }

    .slide-enter-active, .slide-leave-active {
      transition: 200ms;
    }

    .slide-enter, .slide-leave-to {
      transform: translateX(100%);
    }
  }

</style>


